import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  CreateTagDto,
  DeleteResult,
  InferSearchParamsQueryTypeWithArraySorter,
  QueryKey,
  TagDto,
  TagsListDto,
  UpdateTagDto,
} from '@sib/api';

export const useInfiniteTags = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tags.tagsControllerFindAll
  >,
  options?: Partial<
    UseInfiniteQueryOptions<TagsListDto, Error, InfiniteData<TagsListDto>>
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Tags,
      QueryKey.Operation.List,
      payload,
      QueryKey.Operation.Infinite,
    ],
    queryFn: ({ pageParam }) =>
      api.tags.tagsControllerFindAll({
        ...payload,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: payload.page,
  });

export const useTag = (
  uuid: string | undefined,
  options?: Partial<UseQueryOptions<TagDto, Error, TagDto>>
) =>
  useQuery({
    ...options,
    enabled: !!uuid,
    queryKey: [QueryKey.Resource.Tags, QueryKey.Operation.One, uuid],
    queryFn: () => api.tags.tagsControllerFindOne(uuid || ''),
  });

export const useCreateTag = (
  options?: UseMutationOptions<TagDto, Error, CreateTagDto>
) => {
  const qc = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (payload: CreateTagDto) =>
      api.tags.tagsControllerCreate(payload),
    onSuccess: (data, variables, context) => {
      qc.invalidateQueries({
        queryKey: [QueryKey.Resource.Tags],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useUpdateTag = (
  options?: UseMutationOptions<TagDto, Error, { uuid: string } & UpdateTagDto>
) => {
  const qc = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ uuid, ...payload }) =>
      api.tags.tagsControllerUpdate(uuid, payload),
    onSuccess: (data, variables, context) => {
      qc.invalidateQueries({ queryKey: [QueryKey.Resource.Tags] });
      qc.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useDeleteTag = (
  options?: UseMutationOptions<DeleteResult, Error, string>
) => {
  const qc = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (uuid: string) => api.tags.tagsControllerDeleteOne(uuid),
    onSuccess: (data, uuid, context) => {
      qc.invalidateQueries({
        queryKey: [QueryKey.Resource.Tags],
      });
      qc.invalidateQueries({
        queryKey: [QueryKey.Resource.Tags, QueryKey.Operation.One, uuid],
      });
      qc.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });
      options?.onSuccess?.(data, uuid, context);
    },
  });
};
